import React, { useEffect, useState } from 'react';
import {
  withStyles,
  FormControl,
  FormLabel,
  FormGroup,
  Fab,
  Typography,
  Checkbox,
  ListItemText,
  Tooltip,
  TextField,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import CreateIcon from '@material-ui/icons/Create';
import { useTranslation } from 'react-i18next';
import styles from '../styles';
import PF2MAlertDialog from '~/components/PF2MAlertDialog';
import {
  Card, CardBody, CardHeader, CardIcon,
} from '~/components/Card';
import { updateChatGroup } from '~/store/dispatch/actions';
import {
  getEquipmentTypes,
  getEquipmentGroups,
} from '~/store/nref/actions';
import { Form, Formik } from 'formik';
import PF2MAutocomplete from '~/components/PF2MAutocomplete';

const FormControlDefaults = ({
  formLabel, children, classes, isRequired,
}) => (
  <FormControl className={classes.modelParent} required={isRequired}>
    <FormLabel className={classes.formLabel}>{formLabel}</FormLabel>
    <FormGroup>{children}</FormGroup>
  </FormControl>
);

FormControlDefaults.propTypes = {
  classes: PropTypes.object.isRequired,
  formLabel: PropTypes.string.isRequired,
  children: PropTypes.object.isRequired,
  isRequired: PropTypes.bool,
};

FormControlDefaults.defaultProps = {
  isRequired: false,
};

const GroupsManagerModal = ({ classes, selectedGroupMessage, handleClose }) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedEquipments, setSelectedEquipments] = useState(
    selectedGroupMessage?.equipments_id || [],
  );
  const [selectedEquipmentTypes, setSelectedEquipmentTypes] = useState([]);
  const [selectedEquipmentTypeIds, setSelectedEquipmentTypeIds] = useState([]);
  const [selectedEquipmentGroups, setSelectedEquipmentGroups] = useState([]);
  const [selectedEquipmentGroupIds, setSelectedEquipmentGroupIds] = useState([]);
  const [searchEquipment, setSearchEquipment] = useState('');
  const [chatGroupName, setChatGroupName] = useState(
    selectedGroupMessage ? selectedGroupMessage.name : '',
  );
  const [errorMsg, setErrorMsg] = useState('');

  const equipments = useSelector(s => s.manager.equipments).filter(({ active }) => active === 1);
  const equipmentsType = useSelector(s => s.nref.equipmentTypes);
  const equipmentsGroup = useSelector(s => s.nref.equipmentGroups);
  const chatGroups = useSelector(s => s.dispatch.chatGroups);

  const { t: translate } = useTranslation();
  const dispatch = useDispatch();

  const disableSaveButton = chatGroupName === '' || selectedEquipments.length === 0;

  useEffect(() => {
    const fetchData = async () => {
      dispatch(getEquipmentTypes());
      dispatch(getEquipmentGroups());
    };

    if (!dataLoaded) {
      fetchData();
      setDataLoaded(true);
    }
  }, [dispatch, equipmentsType, equipmentsGroup]);

  const handleUpdateItems = () => {
    dispatch(
      updateChatGroup({
        id: selectedGroupMessage?.id,
        name: chatGroupName,
        equipments_id: JSON.stringify(selectedEquipments),
        update_status: selectedGroupMessage !== null ? 'U' : 'I',
      }),
    );
  };

  const renderHeader = () => (
    <div>
      <Typography variant="h4" align="center" gutterBottom>
        {translate('common:MessageGroups')}
      </Typography>
    </div>
  );

  const renderSelectField = ({
    formLabel,
    collection,
    values,
    onChangeFn,
  }) => (
    <FormControlDefaults formLabel={formLabel} classes={classes} isRequired>
      <PF2MAutocomplete
        options={collection?.map(e => ({ value: e.id, label: e.name }))}
        value={values}
        limitTags={1}
        onChange={(_, vals) => onChangeFn(vals)}
      />
    </FormControlDefaults>
  );

  const normalizeEquipments = () => equipments
    .filter(({ id_equip }) => (selectedEquipmentTypes.length > 0
      ? selectedEquipmentTypeIds.includes(id_equip)
      : true))
    .filter(({ id_group }) => (selectedEquipmentGroups.length > 0
      ? selectedEquipmentGroupIds.includes(id_group)
      : true))
    .map(e => ({
      id: e.id,
      name: e.name,
      checked: selectedEquipments.includes(e.id),
    }));

  const normalizedEquipments = normalizeEquipments(equipments)
    .filter(eq => (searchEquipment !== ''
      ? eq.name
        .toLocaleLowerCase()
        .includes(searchEquipment.toLocaleLowerCase())
      : true));

  const handleCheck = ({ id, checked }) => {
    if (checked) {
      setSelectedEquipments([...selectedEquipments, id]);
    } else {
      const removedEquipment = selectedEquipments.filter(se => se !== id);
      setSelectedEquipments(removedEquipment);
    }
  };

  const checkAll = (ids) => {
    const eqNotFiltered = ids.filter(id => !selectedEquipments.includes(id));
    setSelectedEquipments([...selectedEquipments, ...eqNotFiltered]);
  };

  const renderCheckboxField = () => (
    <div
      style={{
        maxHeight: '50vh',
        overflowY: 'scroll',
      }}
    >
      <div
        style={{
          gap: '5px',
          display: 'flex',
          flexWrap: 'wrap',
          flexDirection: 'row',
        }}
      >
        {
          normalizedEquipments.map(s => (
            <Tooltip key={s.id} title={s.name}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                flexGrow: 1,
                width: '8vw',
                textWrapMode: 'nowrap',
                overflowX: 'hidden',
                gap: '5px',
              }}
              >
                <Checkbox
                  checked={selectedEquipments.includes(s.id)}
                  color="primary"
                  name={s.id.toString()}
                  onChange={e => handleCheck({
                    id: s.id,
                    checked: e.target.checked,
                  })
                  }
                />
                <ListItemText secondary={s.name} style={{ padding: 0 }} />
              </div>
            </Tooltip>
          ))}
      </div>
    </div>
  );

  const validateFields = () => {
    const selectedId = selectedGroupMessage?.id;
    const updateFilterFunction = ({ name, id }) => (
      chatGroupName.toLocaleLowerCase() === name.toLocaleLowerCase()
       && selectedGroupMessage.id !== id
    );
    const insertFilterFunction = ({ name }) => (
      chatGroupName.toLocaleLowerCase() === name.toLocaleLowerCase()
    );

    const isUniqueName = chatGroups.filter(
      selectedId !== undefined ? updateFilterFunction : insertFilterFunction,
    );

    if (isUniqueName.length > 0) {
      return false;
    }

    return true;
  };

  const renderFormHeader = () => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-around',
      }}
    >
      <Fab
        variant="extended"
        size="small"
        color="secondary"
        onClick={() => {
          const eqToCheck = normalizedEquipments.map(({ id }) => id);
          checkAll(eqToCheck);
        }}
        className={classes.saveButton}
      >
        {translate('common:SelectAll')}
      </Fab>
      <div>
        <TextField
          label="Nome do Grupo"
          onChange={(e) => {
            if (errorMsg.length > 0) { setErrorMsg(''); }
            setChatGroupName(e.target.value);
          }}
          value={chatGroupName}
        />
        {errorMsg !== '' ? <p className={classes.errorMessage}>{translate(`validation:${errorMsg}`)}</p> : null}
      </div>
      <Fab
        variant="extended"
        size="medium"
        color="secondary"
        onClick={() => {
          const isValid = validateFields();
          if (isValid === false) {
            setErrorMsg('UniqueGroupMessageName');
          } else {
            handleUpdateItems();
            handleClose();
          }
        }}
        className={classes.saveButton}
        disabled={disableSaveButton}
      >
        {translate('common:SaveData')}
      </Fab>
    </div>
  );

  const renderSearchInput = () => (
    <TextField
      id="standard"
      label={translate('common:FilterEquipment')}
      onChange={e => setSearchEquipment(e.target.value)}
      value={searchEquipment}
    />
  );

  const renderFormControl = () => (
    <Formik>
      {() => (
        <Form>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '10px',
              justifyContent: 'space-evenly',
            }}
          >
            {renderSelectField({
              formLabel: translate('common:EquipmentType'),
              values: selectedEquipmentTypes,
              collection: equipmentsType,
              onChangeFn: (values) => {
                const filteredValues = [...values.filter(({ value }) => value >= 0)];
                setSelectedEquipmentTypes(filteredValues);
                setSelectedEquipmentTypeIds(filteredValues.map(({ value }) => value));
              },
            })}
            {renderSelectField({
              formLabel: translate('common:EquipmentGroup'),
              values: selectedEquipmentGroups,
              collection: equipmentsGroup.filter(eg => (
                selectedEquipmentTypes.length > 0
                  ? selectedEquipmentTypeIds.includes(eg.id_equipament)
                  : eg
              )),
              onChangeFn: (values) => {
                const filteredValues = [...values.filter(({ value }) => value >= 0)];
                setSelectedEquipmentGroups(filteredValues);
                setSelectedEquipmentGroupIds(filteredValues.map(({ value }) => value));
              },
            })}
            {renderSearchInput()}
          </div>
          <hr />
          {renderFormHeader()}
          {renderCheckboxField()}
        </Form>
      )}
    </Formik>
  );

  return (
    <div style={{ height: '85vh', width: '85vw' }}>
      <Card style={{ height: '100%', overflow: 'hidden' }}>
        <CardHeader icon>
          <CardIcon color="warning">
            <CreateIcon />
          </CardIcon>
        </CardHeader>
        <CardBody>
          {renderHeader()}
          <hr />
          <div>{renderFormControl()}</div>
        </CardBody>
      </Card>
      <PF2MAlertDialog
        hasCancel={false}
        confirmText={translate('common:Understood')}
        description={translate('validation:ThereAreBlankFields')}
        open={isModalOpen}
        onConfirm={() => setModalOpen(false)}
      />
    </div>
  );
};

GroupsManagerModal.propTypes = {
  classes: PropTypes.object.isRequired,
  selectedGroupMessage: PropTypes.array,
  handleClose: PropTypes.func.isRequired,
};

GroupsManagerModal.defaultProps = {
  selectedGroupMessage: [],
};

export default withStyles(styles)(GroupsManagerModal);
